export default class pegOfflineNotification extends HTMLElement {
	#notifications;
	#abortController;
	#signal;

	async connectedCallback() {
		await new Promise(requestAnimationFrame);
		this.#abortController = new AbortController();
		this.#signal = this.#abortController.signal;

		this.#notifications = {
			offline: {
				element: this.querySelector('#offlineError'),
				show: () => this.#showNotification('offline'),
			},
			lieFi: {
				element: this.querySelector('#lieFiNotification'),
				show: () => this.#showNotification('lieFi'),
				button: this.querySelector('#lieFiNotification a'),
			},
		};

		this.#init();
	}

	async disconnectedCallback() {
		await new Promise(requestAnimationFrame);
		this.#abortController?.abort();
	}

	#init() {
		this.#checkOnlineState();
		document.addEventListener('ajaxShellPage:initDone', () => this.#checkCachedPage(), { signal: this.#signal });

		document.addEventListener(
			'serviceworker:pageFromNetwork',
			() => {
				this.#hideAllNotifications();
				document.querySelector('html')?.removeAttribute('data-origin');
			},
			{ signal: this.#signal },
		);
		document.addEventListener(
			'serviceworker:triggerOfflineAttention',
			() => {
				this.#triggerOfflineAttention();
			},
			{ signal: this.#signal },
		);

		// Setup animation end event
		this.#notifications.offline.element?.addEventListener('animationend', () => this.#notifications.offline.element.classList.remove('attention'), {
			signal: this.#signal,
		});

		// Setup reload button
		this.#notifications.lieFi.button?.addEventListener(
			'click',
			() => {
				this.#handleReloadClick(event);
			},
			{ signal: this.#signal },
		);
	}

	#checkOnlineState() {
		if (!navigator.onLine) {
			this.#handleOfflineMode();
		}

		window.addEventListener('offline', () => this.#handleOfflineMode(), { signal: this.#signal });
		window.addEventListener('online', () => this.#handleOnlineMode(), { signal: this.#signal });
	}

	#handleOfflineMode() {
		document.documentElement.classList.add('is-offline');
		this.#notifications.offline.show();
		this.#setupOnlineOnlyElements(true);
	}

	#handleOnlineMode() {
		document.documentElement.classList.remove('is-offline');
		this.#hideAllNotifications();
		this.#setupOnlineOnlyElements(false);
	}

	#setupOnlineOnlyElements(add) {
		const onlineOnlyElements = document.querySelectorAll('.js-onlineOnly');
		onlineOnlyElements.forEach((element) => {
			if (add) {
				element.addEventListener('click', (event) => this.onOnlineOnlyElementClick(event), { signal: this.#signal });
			} else {
				element.removeEventListener('click', this.onOnlineOnlyElementClick);
			}
		});
	}

	onOnlineOnlyElementClick(event) {
		event.preventDefault();
		event.stopPropagation();
		this.#triggerOfflineAttention();
	}

	#triggerOfflineAttention() {
		const element = this.#notifications.offline.element;
		if (!element) return;

		element.classList.remove('noDisplay');
		element.classList.remove('attention');
		// Force reflow to ensure animation can be triggered again
		void element.offsetWidth;
		element.classList.add('attention');
	}

	// Shows the appropriate notification based on online/offline state
	#showNotification(type) {
		this.#hideAllNotifications();

		if (!this.#notifications[type]?.element) return;

		this.#notifications[type].element.classList.remove('noDisplay');

		if (type === 'lieFi') {
			// Add auto-hide behavior for lieFi
			document.addEventListener('scroll', () => this.#handlePageInteraction(), {
				once: true,
				signal: this.#signal,
			});

			// Auto-hide notification after timeout
			setTimeout(() => this.#handlePageInteraction(), 15000);
		}
	}

	#hideAllNotifications() {
		Object.values(this.#notifications).forEach((notification) => {
			notification.element?.classList.add('noDisplay');
		});
	}

	// Check if we're on a cached page and show appropriate notification
	#checkCachedPage() {
		if (this.#isCachedPage()) {
			if (navigator.onLine) {
				this.#notifications.lieFi.show();
			} else {
				this.#notifications.offline.show();
			}
		}
	}

	#isCachedPage() {
		return document.querySelector('html[data-origin=cacheStorage]') !== null || document.querySelector('html[data-origin=cacheStorageAjax]') !== null;
	}

	#handlePageInteraction() {
		this.#hideAllNotifications();
		document.removeEventListener('scroll', () => this.#handlePageInteraction);
	}

	#handleReloadClick(event) {
		event.preventDefault();
		document.location.reload();
	}
}
